import type { ChainedCommands, Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';

// The library's `preservedStyle` mark is serialized as an inline <span style>, so
// it is the only way to style part of a block rather than the whole block.
export const INLINE_STYLE_MARK = 'preservedStyle';

export type InlineStyleProperty = 'color' | 'fontSize' | 'lineHeight' | 'backgroundColor';

const CSS_NAME: Record<InlineStyleProperty, string> = {
	color: 'color',
	fontSize: 'font-size',
	lineHeight: 'line-height',
	backgroundColor: 'background-color',
};

const parseDeclarations = (style: string): Map<string, string> => {
	const declarations = new Map<string, string>();
	style
		.split(';')
		.map((declaration) => declaration.trim())
		.filter(Boolean)
		.forEach((declaration) => {
			const separator = declaration.indexOf(':');
			if (separator > 0) {
				declarations.set(
					declaration.slice(0, separator).trim().toLowerCase(),
					declaration.slice(separator + 1).trim()
				);
			}
		});
	return declarations;
};

const serializeDeclarations = (declarations: Map<string, string>): string =>
	[...declarations.entries()].map(([name, value]) => `${name}: ${value}`).join('; ');

const markStyle = (editor: Editor): string => {
	const style = editor.getAttributes(INLINE_STYLE_MARK).style;
	return typeof style === 'string' ? style : '';
};

export const readInlineStyle = (
	editor: Editor,
	property: InlineStyleProperty
): string | undefined => parseDeclarations(markStyle(editor)).get(CSS_NAME[property]);

// focus() collapses the selection, so a second edit would land on nothing.
const withPreservedSelection = (editor: Editor, apply: (chain: ChainedCommands) => void): void => {
	const { selection } = editor.state;
	const isNodeSelection = selection instanceof NodeSelection;
	const { from, to } = selection;
	const chain = editor.chain().focus();
	apply(chain);
	if (isNodeSelection) {
		chain.setNodeSelection(from);
	} else {
		chain.setTextSelection({ from, to });
	}
	chain.run();
};

export const writeInlineStyle = (
	editor: Editor,
	property: InlineStyleProperty,
	value: string
): void => {
	const declarations = parseDeclarations(markStyle(editor));
	declarations.set(CSS_NAME[property], value);
	withPreservedSelection(editor, (chain) =>
		chain.setMark(INLINE_STYLE_MARK, { style: serializeDeclarations(declarations) })
	);
};

export const toggleInlineMark = (editor: Editor, mark: string): void => {
	withPreservedSelection(editor, (chain) => chain.toggleMark(mark));
};

export const parseInlineStyle = (style: string): Map<string, string> => parseDeclarations(style);

export const formatInlineStyle = (declarations: Map<string, string>): string =>
	serializeDeclarations(declarations);
